import { getToken } from '@flowus/common/utils/cookie';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';
import type { IconData } from '@next-space/fe-api-idl';
import { generateCustomIconUrlByPath, getIsDarkMode } from '@flowus/common';
import { urlFetcher } from 'src/utils/url-fetcher';
import { getImageCdnUrl } from 'src/common/utils/url-utils';

interface AIResponse {
  code?: number;
  message?: string;
  markdown?: string;
  html?: string;
  done?: boolean;
  error?: string;
}
// Track all running streams and their controllers
const runningStreams = new Map<string, AbortController>();

export class StreamProcessor {
  private decoder = new TextDecoder();

  constructor(private onChunk: (chunk: AIResponse) => void) {}

  public async processStream(reader: ReadableStreamDefaultReader<Uint8Array>) {
    try {
      let done = false; // 初始化 done 状态
      let buffer = '';
      while (!done) {
        const { done: isDone, value } = await reader.read();
        done = isDone; // 更新 done 状态

        if (done) break;
        const chunk = this.decoder.decode(value, { stream: true });
        buffer += chunk;
        // 处理缓冲区中的所有完整的SSE消息
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // 最后一个可能不完整，保留到下一次
        for (const line of lines) {
          if (line.trim() && line.startsWith('data:')) {
            const rawData = line.substring(5).trim();
            try {
              const data = JSON.parse(rawData);
              if (data.status === 'complete') {
                this.onChunk({ markdown: data.content });
              } else if (data.status === 'done') {
                this.onChunk({ done: true });
              } else if (data.status === 'heartbeat') {
                //
              } else if (data.status === 'error') {
                this.onChunk({ error: data.message });
              } else {
                this.onChunk({ message: data.message });
              }
              if (data.code === 1000) {
                throw new Error(data.content || 'AI response error');
              }
            } catch (e) {
              // 尝试看是否数据本身是HTML
              if (rawData.includes('<html') || rawData.includes('<!DOCTYPE')) {
                this.onChunk({ html: rawData });
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error streaming AI response:', error);
    } finally {
      reader.releaseLock();
    }
  }
}

export async function extractCodeBlocks(
  markdown: string
): Promise<{ language: string | null; code: string }[]> {
  const codeBlocks: { language: string | null; code: string }[] = [];

  const root = unified()
    .use(remarkParse) // 将 Markdown 转换为语法树
    .use(remarkGfm) // 支持 GFM (GitHub Flavored Markdown)
    .parse(markdown); // 解析 Markdown 字符串
  visit(root, 'code', (node) => {
    codeBlocks.push({
      language: node.lang || null,
      code: node.value,
    });
  });
  return codeBlocks;
}

export const startGenerateAiCover = async (
  body: any, // 后端把idl的type删掉了
  onProcess: (resp: AIResponse) => void
) => {
  try {
    const controller = new AbortController();
    runningStreams.set(body.blockId, controller);
    const response = await fetch(`/api/rag/page/render-sse`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const processor = new StreamProcessor(onProcess);
    await processor.processStream(reader);
  } catch (error) {
    console.error('Error streaming AI response:', error);
  } finally {
    runningStreams.delete(body.blockId);
  }
};

export const stopAICover = () => {
  runningStreams.forEach((v) => {
    v.abort();
  });
  runningStreams.clear();
};

export const getSpaceIconUrl = async (icon?: IconData) => {
  if (!icon) return;
  if (icon.type === 'http' && icon.value) {
    return icon.value;
  } else if (icon.type === 'icon' && icon.value) {
    return generateCustomIconUrlByPath(getIsDarkMode(), icon.value);
  } else if (icon.type === 'upload' && icon.value) {
    return getImageCdnUrl(icon.value);
  } else if (icon.type === 'emoji' && icon.value) {
    return icon.value;
  }
  return '';
};
